import { useCallback, useEffect, useState } from "react";
import api, { isCancel } from "../services/api/axios";

const defaultGetItems = (responseData) =>
  Array.isArray(responseData) ? responseData : responseData?.data || [];

const defaultGetTotal = (responseData, items) =>
  responseData?.total ?? responseData?.count ?? items.length;

export default function usePaginatedApiData({
  endpoint,
  params,
  errorMessage,
  getItems = defaultGetItems,
  getTotal = defaultGetTotal,
  enabled = true,
  onError,
}) {
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(
    async (signal) => {
      if (!enabled) return;

      setLoading(true);
      setError(null);

      try {
        const res = await api.get(endpoint, { params, signal });
        const items = getItems(res.data);

        setData(items);
        setTotalCount(getTotal(res.data, items));
        return items;
      } catch (err) {
        if (isCancel(err)) return;
        onError?.(err);
        setError(errorMessage);
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [enabled, endpoint, errorMessage, getItems, getTotal, onError, params],
  );

  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();
    fetchData(controller.signal);

    return () => controller.abort();
  }, [enabled, fetchData]);

  return {
    data,
    totalCount,
    loading,
    error,
    refetch: fetchData,
    setData,
  };
}
